import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { EVersionType } from 'src/app/shared/enums/rule.enums';
import { IResponseList } from 'src/app/shared/interfaces/response.interfaces';
import {
  IRuleVersion,
  ILevels,
  ICategory,
  IRule,
  IRules,
  IBucket,
  ITemplate,
  IRuleSaveResponse,
} from 'src/app/shared/interfaces/rule.interfaces';
import { SessionService } from 'src/app/core/session.service';
import { UserService } from './user.service';
import { PERMISSIONS } from 'src/app/core/roles';

const API_BASE = '/bff/v1';

@Injectable({ providedIn: 'root' })
export class RuleService {
  constructor(
    private httpClient: HttpClient,
    private session: SessionService,
    private userService: UserService
  ) {}

  /*
    https://docs.closecare.com.br/docs/closecare-api/2203f2a2dcd7d-list-rule-versions
  */
  getVersionsList({
    page,
    size,
    sort_by = 'created_at,desc',
    version_type,
  }: {
    page: number;
    size: number;
    sort_by?: string;
    version_type?: EVersionType;
  }): Observable<IResponseList<IRuleVersion>> {
    let params = new HttpParams();

    params = params.set('page', page);
    params = params.set('size', size);
    params = params.set('sort_by', sort_by);
    if (version_type) params = params.set('version_type', version_type);

    return this.httpClient.get<IResponseList<IRuleVersion>>(
      `${API_BASE}/rule-versions`,
      { params }
    );
  }

  /*
    https://docs.closecare.com.br/docs/closecare-api/a4d73b38d32d4-read-rule-versions-by-id
  */
  getVersion(versionId: number): Observable<IRuleVersion> {
    return this.httpClient.get<IRuleVersion>(
      `${API_BASE}/rule-versions/${versionId}`
    );
  }

  /*
    https://closecare.stoplight.io/docs/closecare-api/153a357dce358-create-rule-versions
  */
  createVersion(body: any): Observable<IRuleVersion> {
    return this.httpClient.post<IRuleVersion>(
      `${API_BASE}/rule-versions`,
      body
    );
  }

  /*
    https://docs.closecare.com.br/docs/closecare-api/a4d73b38d32d4-read-rule-versions-by-id
  */
  duplicateVersion(versionId: number): Observable<IRuleVersion> {
    return this.httpClient.post<IRuleVersion>(
      `${API_BASE}/rule-versions/${versionId}/duplicate`,
      {}
    );
  }

  /*
    https://docs.closecare.com.br/docs/closecare-api/9019024f3e031-delete-rule-versions
  */
  deleteVersion(versionId: number): Observable<IRuleVersion> {
    return this.httpClient.delete<IRuleVersion>(
      `${API_BASE}/rule-versions/${versionId}`
    );
  }

  /*
    https://docs.closecare.com.br/docs/closecare-api/c7af1e55408ce-get-levels-with-custom-rules
  */
  getLevels(versionId: number): Observable<ILevels> {
    return this.httpClient.get<ILevels>(
      `${API_BASE}/versions/${versionId}/rule-applications/levels`
    );
  }

  /*
    https://docs.closecare.com.br/docs/closecare-api/aef6dd6973443-list-template-categories
  */
  getCategoriesList(
    page: number,
    size: number,
    sort_by: string
  ): Observable<IResponseList<ICategory>> {
    let params = new HttpParams();

    params = params.set('page', page);
    params = params.set('size', size);
    params = params.set('sort_by', sort_by);

    return this.httpClient.get<IResponseList<ICategory>>(
      `${API_BASE}/template-categories`
    );
  }

  /*
    https://docs.closecare.com.br/docs/closecare-api/c086b9d3d91b3-list-rule-applications
  */
  getRulesList({
    versionId,
    all = false,
    page = null,
    size = null,
    sort_by,
    rule_id,
    labor_union_id,
    location_group_id,
    location_id,
    add_children,
  }: {
    versionId: number;
    all?: boolean;
    page?: number;
    size?: number;
    sort_by?: string;
    rule_id?: number;
    labor_union_id?: number | null;
    location_group_id?: number | null;
    location_id?: number | null;
    add_children?: boolean;
  }): Observable<IRules> {
    let params = new HttpParams();

    if (all) {
      params = params.set('all', all);
    } else {
      params = params.set('page', page);
      params = params.set('size', size);
    }
    if (rule_id) {
      params = params.set('rule_id', rule_id);
    }
    if (labor_union_id !== undefined)
      params = params.set('labor_union_id', labor_union_id);
    if (location_group_id !== undefined)
      params = params.set('location_group_id', location_group_id);
    if (location_id !== undefined)
      params = params.set('location_id', location_id);

    if (add_children) params = params.set('add_children', add_children);

    if (sort_by) params = params.set('sort_by', sort_by);

    return this.httpClient.get<IRules>(
      `${API_BASE}/versions/${versionId}/rule-applications`,
      { params }
    );
  }

  /*
    https://closecare.stoplight.io/docs/closecare-api/7e2d1897da84b-read-rule-applications-by-id
  */
  getRule({ ruleId, versionId }: { versionId: number; ruleId: number }) {
    return this.httpClient.get<IRule>(
      `${API_BASE}/versions/${versionId}/rules/${ruleId}`
    );
  }

  /*
    https://closecare.stoplight.io/docs/closecare-api/e989b63c90bc7-list-templates
  */
  getTemplateList({
    page = null,
    size = null,
    sort_by,
    all = false,
  }: {
    page?: number | null;
    size?: number | null;
    sort_by?: string;
    all?: boolean;
  }): Observable<IResponseList<ITemplate>> {
    let params = new HttpParams();

    params = params.set('page', page);
    params = params.set('size', size);
    if (sort_by) params = params.set('sort_by', sort_by);
    params = params.set('all', all);

    return this.httpClient.get<IResponseList<ITemplate>>(
      `${API_BASE}/templates`,
      { params }
    );
  }

  /*
    https://closecare.stoplight.io/docs/closecare-api/c5f4174592997-read-templates-by-id
  */
  getTemplate(templateId: number): Observable<ITemplate> {
    return this.httpClient.get<ITemplate>(
      `${API_BASE}/templates/${templateId}`
    );
  }

  createRule({
    versionId,
    ...body
  }: RuleCreateParams): Observable<IRuleSaveResponse> {
    return this.httpClient.post<IRuleSaveResponse>(
      `${API_BASE}/versions/${versionId}/rules-with-applications`,
      body
    );
  }

  reorderRules({ versionId, ...body }: RuleReorderParams) {
    return this.httpClient.post<IBucket[]>(
      `${API_BASE}/versions/${versionId}/rules-with-applications/reorder`,
      body
    );
  }

  /*
    https://closecare.stoplight.io/docs/closecare-api/33fb8e10a7cd0-duplicate-a-rule-and-apply-the-operation-to-an-organization-hierarchy-level
  */
  duplicateRules(
    versionId: number,
    id: number,
    body: RuleCommon
  ): Observable<IBucket> {
    return this.httpClient.post<IBucket>(
      `${API_BASE}/versions/${versionId}/rules-with-applications/${id}/duplicate`,
      body
    );
  }

  applyRule({
    versionId,
    id,
    location_group_id = null,
    location_id = null,
    labor_union_id = null,
    replicate_down = false,
  }: RuleApplyParams) {
    return this.httpClient.post<IBucket>(
      `${API_BASE}/versions/${versionId}/rules-with-applications/${id}/apply`,
      {
        location_group_id,
        location_id,
        labor_union_id,
        replicate_down,
      }
    );
  }

  editRule({
    versionId,
    id,
    location_group_id = null,
    location_id = null,
    labor_union_id = null,
    ...body
  }: RuleEditParams) {
    return this.httpClient.post<IBucket>(
      `${API_BASE}/versions/${versionId}/rules-with-applications/${id}/edit`,
      {
        location_group_id,
        location_id,
        labor_union_id,
        ...body,
      }
    );
  }

  deleteRule({
    id,
    versionId,
    location_group_id = null,
    location_id = null,
    labor_union_id = null,
    replicate_down = false,
  }: RuleDeleteParams) {
    return this.httpClient.post<IBucket>(
      `${API_BASE}/versions/${versionId}/rules-with-applications/${id}/delete`,
      {
        location_group_id,
        location_id,
        labor_union_id,
        replicate_down,
      }
    );
  }

  /*
    https://docs.closecare.com.br/docs/closecare-api/0ae781f6217c5-list-rules
  */
  getBucketList({
    versionId,
    page = null,
    size = null,
    sort_by = null,
    all = false,
  }: {
    versionId: number;
    page?: number | null;
    size?: number | null;
    sort_by?: string | null;
    all?: boolean;
  }) {
    let params = new HttpParams();

    params = params.set('page', page);
    params = params.set('size', size);
    if (sort_by) params = params.set('sort_by', sort_by);
    params = params.set('all', all);

    return this.httpClient.get<IResponseList<IRule>>(
      `${API_BASE}/versions/${versionId}/rules`,
      { params }
    );
  }

  publishVersion(versionId: number) {
    return this.httpClient.post<IBucket>(
      `${API_BASE}/rule-versions/${versionId}/publish`,
      {}
    );
  }
  canEdit(): Observable<boolean> {
    return this.session.permissions$.pipe(
      map((permissions) =>
        this.userService.hasPermissions(
          [PERMISSIONS.spicedb_rules_w],
          permissions
        )
      )
    );
  }
}

interface RuleCommon {
  location_group_id: number | null;
  location_id: number | null;
  labor_union_id: number | null;
  replicate_down: boolean;
}

interface RuleApplyParams extends RuleCommon {
  id: string | number;
  versionId: string | number;
}

interface RuleCreateParams extends RuleCommon {
  versionId: string | number;
  template_id: number;
  name: string;
  description: string;
  values: Record<string, unknown>;
}

interface RuleDeleteParams extends RuleCommon {
  id: string | number;
  versionId: string | number;
}

interface RuleEditParams extends Omit<RuleCommon, 'replicate_down'> {
  id: string | number;
  versionId: string | number;
  name?: string;
  description?: string;
  values?: Record<string, unknown>;
}

interface RuleReorderParams extends Omit<RuleCommon, 'replicate_down'> {
  versionId: string | number;
  items: {
    ruleId: number;
    order: number;
  }[];
}
